import * as React from "react";
import { FC, useEffect, useMemo, useState } from "react";
import { connect } from "react-redux";
import ReactTable, { RowInfo } from "react-table";
import { bindActionCreators } from "redux";
import { InlineLoading } from "src/components/common";
import { MembershipTypesBreadcrumbs } from "src/helpers/breadcrumbHelper";
import { SettingsGeneralSubMenu } from "src/helpers/subMenuHelper";
import useNavigate from "src/hooks/useNavigate";
import * as MembershipTypeActions from "../../../actions/membershipTypes";
import * as NavigationActions from "../../../actions/navigation";
import { FormFilterInput } from "../../../components/form";
import { Link } from "../../../components/routing/index";
import {
    FontAwesomeIcon,
    FontAwesomeIcons,
} from "../../../constants/fontAwesomeIcons";
import {
    DEFAULT_PAGE_SIZE,
    PAGE_SIZE_OPTIONS,
} from "../../../constants/reactTableConstants";
import * as Dtos from "../../../dtos/Tmd.dtos";
import { RequestState } from "../../../enumerations/RequestState";
import * as AuthenticationHelper from "../../../helpers/authentication";
import { IFormFilter, ITmdState } from "../../../reducers/index";
import { LookupsLayout } from "../../index";

import "./MembershipTypesPage.scss";

interface MembershipTypesPageProps {
    formFilter: IFormFilter;
    membershipType: Dtos.MembershipType[];
    loadingMembershipTypes: boolean;
    loadMembershipTypesSuccess: boolean;
    loadMembershipTypesFailure: boolean;
    loadMembershipTypesErrorCode: string;
    permissions?: Dtos.PersonnelTmdPermissions[];
    clearMembershipTypes: MembershipTypeActions.IMembershipTypeClearActionCreator;
    loadMembershipTypes: MembershipTypeActions.IMembershipTypeLoadActionCreator;
    filter: MembershipTypeActions.IMembershipTypeFilterActionCreator;
    setBreadcrumbs: NavigationActions.ISetBreadcrumbsActionCreator;
    setDrawerItems: NavigationActions.ISetDrawerItemsActionCreator;
}

const MembershipTypesPage: FC<MembershipTypesPageProps> = ({
    formFilter,
    membershipType,
    loadingMembershipTypes,
    loadMembershipTypesSuccess,
    loadMembershipTypesFailure,
    loadMembershipTypesErrorCode,
    permissions,
    clearMembershipTypes,
    loadMembershipTypes,
    filter,
    setBreadcrumbs,
    setDrawerItems
}) => {
    //
    // -- Hooks --
    const navigate = useNavigate();

    //
    // -- States --
    const [initalised, setInitalised] = useState<boolean>(false);

    //
    // -- Variables --
    const showMembershipTypes =
        !loadingMembershipTypes && loadMembershipTypesSuccess;


    // Load membership types
    useEffect(() => {
        if (!initalised) {
            setDrawerItems(SettingsGeneralSubMenu(permissions));
            setBreadcrumbs(MembershipTypesBreadcrumbs());

            loadMembershipTypes();

            setInitalised(true);
        }
    }, [
        initalised,
        setDrawerItems,
        SettingsGeneralSubMenu,
        permissions,
        setBreadcrumbs,
        MembershipTypesBreadcrumbs,
        loadMembershipTypes,
        setInitalised,
    ]);

    // Navigate to error page on load failure
    useEffect(() => {
        if (loadMembershipTypesFailure) {
            navigate(`/error/${loadMembershipTypesErrorCode}`);
        }
    }, [loadMembershipTypesFailure, loadMembershipTypesErrorCode, navigate]);

    // Cleanup effect to clear the membership types on component unmount
    useEffect(() => {
        return () => {
            clearMembershipTypes();
        };
    }, [clearMembershipTypes]);

    const createTable = useMemo(() => {
        if (!membershipType || membershipType.length == 0) {
            return (
                <div className="card card-shadow p-3">
                    <div className="alert alert-info d-flex mb-0">
                        <div className="mr-2">
                            <FontAwesomeIcon
                                icon={FontAwesomeIcons.Solid.INFO_SQUARE}
                                fixedWidth
                            />
                        </div>
                        <span>
                            There are currently no membership types to view.
                        </span>
                    </div>
                </div>
            );
        }

        const showPagination = membershipType.length > DEFAULT_PAGE_SIZE;

        return (
            <div className="table-wrapper">
                <ReactTable
                    data={membershipType}
                    pageSizeOptions={PAGE_SIZE_OPTIONS}
                    defaultPageSize={
                        showPagination
                            ? DEFAULT_PAGE_SIZE
                            : membershipType.length
                    }
                    pageSize={
                        showPagination ? undefined : membershipType.length
                    }
                    showPagination={showPagination}
                    className="-striped -highlight -clickable"
                    noDataText="There are currently no membershipType to display"
                    resizable={false}
                    getTdProps={(
                        _state,
                        rowInfo: RowInfo,
                        _column,
                        _instance
                    ) => {
                        return {
                            onClick: (_e, handleOriginal) => {
                                const url: string = `/settings/general/membershiptypes/${rowInfo.original.id}`;
                                navigate(url);

                                if (handleOriginal) {
                                    handleOriginal();
                                }
                            },
                        };
                    }}
                    columns={[
                        {
                            id: "id",
                            Header: " ",
                            accessor: "id",
                            maxWidth: 50,
                            Cell: (_props) => (
                                <FontAwesomeIcon
                                    size="lg"
                                    icon={FontAwesomeIcons.Light.LOOKUP}
                                    fixedWidth
                                />
                            ),
                        },
                        {
                            id: "value",
                            Header: "Name",
                            accessor: "value",
                        },
                    ]}
                />
            </div>
        );
    }, [membershipType, DEFAULT_PAGE_SIZE, PAGE_SIZE_OPTIONS, navigate]);

    const renderContent = useMemo(() => {
        return (
            <div>
                <div className="row">
                    <div className={"col-auto mb-2"}>
                        <h1>Membership Types</h1>
                    </div>
                    <div
                        className="col d-flex justify-content-end align-items-stretch"
                        style={{ height: "38px" }}
                    >
                        <FormFilterInput
                            formFilter={formFilter}
                            onFilter={(e) => filter(e)}
                        />

                        {AuthenticationHelper.hasPermission(
                            permissions,
                            Dtos.Permission.MembershipTypesCreate
                        ) && (
                            <Link
                                className={"btn btn-primary"}
                                url={"/settings/general/membershiptypes/create"}
                            >
                                Create
                            </Link>
                        )}
                    </div>
                </div>
                {!showMembershipTypes ? (
                    <InlineLoading absoluteCenter={true} size={50} />
                ) : null}
                {showMembershipTypes ? (
                    <div className="card">
                        <div className="patient-tables">{createTable}</div>
                    </div>
                ) : null}
            </div>
        );
    }, [formFilter, filter, permissions, showMembershipTypes, createTable]);

    return (
        <LookupsLayout
            loading={loadingMembershipTypes}
            permission={Dtos.Permission.MembershipTypesView}
        >
            {renderContent}
        </LookupsLayout>
    );
};

const mapStateToProps = (
    state: ITmdState
): Partial<MembershipTypesPageProps> => {
    let permissions = state.user.userSummary
        ? state.user.userSummary.permissions
        : [];

    return {
        formFilter: state.membershipTypes.formFilter,
        membershipType: state.membershipTypes.formFilteredData,
        loadingMembershipTypes:
            state.membershipTypes.loadState &&
            state.membershipTypes.loadState.status == RequestState.Pending,
        loadMembershipTypesSuccess:
            state.membershipTypes.loadState &&
            state.membershipTypes.loadState.status == RequestState.Success,
        loadMembershipTypesFailure:
            state.membershipTypes.loadState &&
            state.membershipTypes.loadState.status == RequestState.Failure,
        loadMembershipTypesErrorCode: state.membershipTypes.loadState.errorCode,
        permissions: permissions,
    };
};

const mapDispatchToProps = (dispatch): Partial<MembershipTypesPageProps> => {
    return {
        loadMembershipTypes: bindActionCreators(
            MembershipTypeActions.LoadMembershipType,
            dispatch
        ),
        clearMembershipTypes: bindActionCreators(
            MembershipTypeActions.Clear,
            dispatch
        ),
        filter: bindActionCreators(MembershipTypeActions.Filter, dispatch),
        setBreadcrumbs: bindActionCreators(
            NavigationActions.SetBreadcrumbs,
            dispatch
        ),
        setDrawerItems: bindActionCreators(
            NavigationActions.SetDrawerItems,
            dispatch
        )
    };
};

export default connect(
    mapStateToProps,
    mapDispatchToProps
)(MembershipTypesPage);
